import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import {
  LogOut, Plus, Trash2, Building2, QrCode, Download, Copy, Star, RefreshCw,
  Loader2, ScanLine, Users, TrendingUp, X, Power, Save, ExternalLink,
  Sparkles, Upload, Award, TrendingDown, MessageSquare, ChevronLeft, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDay = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

const chartTooltip = {
  contentStyle: { background: "#16130f", border: "1px solid #2c2620", borderRadius: 2, fontSize: 12 },
  labelStyle: { color: "#a8a29e" },
};

function NavHeader({ onLogout }) {
  return (
    <header className="border-b border-border sticky top-0 bg-background/70 backdrop-blur-2xl z-40">
      <div className="max-w-[100rem] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <span className="font-heading font-black text-lg tracking-tighter">GMP<span className="text-accent">.</span></span>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground transition-colors">Demandes</Link>
            <Link to="/admin/blog" className="text-muted-foreground hover:text-foreground transition-colors">Articles</Link>
            <Link to="/admin/cockpit" className="text-warm font-medium">Cockpit Avis</Link>
          </nav>
        </div>
        <button onClick={onLogout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <LogOut size={16} /> Déconnexion
        </button>
      </div>
    </header>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-surface border border-border p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</span>
        <Icon size={15} className="text-accent" />
      </div>
      <p className="font-heading font-black text-2xl">{value}</p>
    </div>
  );
}

function Stars({ rating }) {
  if (rating == null) return <span className="text-muted-foreground text-sm">—</span>;
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={15} className={n <= Math.round(rating) ? "fill-warm text-warm" : "text-border"} />
      ))}
      <span className="ml-1 font-heading font-bold">{rating.toFixed(1)}</span>
    </div>
  );
}

const SENT = {
  positif: { c: "#34d399", label: "Positif" },
  neutre: { c: "#a8a29e", label: "Neutre" },
  negatif: { c: "#ef4444", label: "Négatif" },
};

function AiAnalysisView({ analysis, loading }) {
  if (loading) {
    return <div className="mt-6 border-t border-border pt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Analyse IA en cours…</div>;
  }
  if (!analysis) return null;
  const sb = analysis.sentiment_breakdown || {};
  return (
    <div className="mt-6 border-t border-border pt-6 space-y-5" data-testid="ai-analysis">
      <p className="text-sm text-foreground/90 leading-relaxed">{analysis.summary}</p>
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Sentiment global</p>
        <div className="flex h-3 w-full overflow-hidden rounded">
          <div style={{ width: `${sb.positif || 0}%`, background: "#34d399" }} />
          <div style={{ width: `${sb.neutre || 0}%`, background: "#6b6359" }} />
          <div style={{ width: `${sb.negatif || 0}%`, background: "#ef4444" }} />
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 inline-block" style={{ background: "#34d399" }} /> Positif {sb.positif || 0}%</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 inline-block" style={{ background: "#6b6359" }} /> Neutre {sb.neutre || 0}%</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 inline-block" style={{ background: "#ef4444" }} /> Négatif {sb.negatif || 0}%</span>
        </div>
      </div>
      <div className="space-y-2">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Thèmes récurrents</p>
        {(analysis.themes || []).map((t, i) => (
          <div key={i} className="flex items-start gap-3 border border-border p-3">
            <span className="font-heading font-black text-lg w-12 shrink-0 text-right" style={{ color: SENT[t.sentiment]?.c || "#a8a29e" }}>{t.share}%</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{t.theme}</span>
                <span className="font-mono text-[9px] uppercase px-1.5 py-0.5" style={{ color: SENT[t.sentiment]?.c, border: `1px solid ${SENT[t.sentiment]?.c}55` }}>{SENT[t.sentiment]?.label || t.sentiment}</span>
              </div>
              {t.example && <p className="text-xs text-muted-foreground italic mt-1">{t.example}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RankRow({ a, onOpen, best }) {
  return (
    <button onClick={() => onOpen(a.id)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors text-left">
      <Star size={14} className={best ? "fill-warm text-warm" : "text-border"} />
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{a.name}</p>
        <p className="text-[11px] text-muted-foreground">{a.city || "—"} · {a.review_count ?? 0} avis</p>
      </div>
      <span className="font-heading font-bold" style={{ color: best ? "#ff9e64" : "#ef4444" }}>{a.rating?.toFixed(1) ?? "—"}</span>
    </button>
  );
}

function ClientInsights({ client, onOpenAgency, onImported }) {
  const [insights, setInsights] = useState(null);
  const [latest, setLatest] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const [i, l, a] = await Promise.all([
        api.get(`/admin/clients/${client.id}/insights`),
        api.get(`/admin/clients/${client.id}/latest-reviews`),
        api.get(`/admin/clients/${client.id}/analysis`),
      ]);
      setInsights(i.data);
      setLatest(l.data);
      setAnalysis(a.data);
    } catch (err) { /* noop */ }
  };

  useEffect(() => { setAnalysis(null); load(); /* eslint-disable-next-line */ }, [client.id]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/admin/clients/${client.id}/reviews/refresh-all`);
      toast.success(`${r.data.updated} agence(s) synchronisée(s)`);
      load();
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/admin/clients/${client.id}/analyze`);
      setAnalysis(r.data);
      toast.success("Analyse IA terminée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const lines = String(ev.target.result).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      let rows = lines;
      if (lines.length && /nom|name/i.test(lines[0]) && /ville|city/i.test(lines[0])) rows = lines.slice(1);
      const items = rows.map((l) => { const p = l.split(/[;,\t]/).map((s) => s?.trim()); return { name: p[0], city: p[1] }; }).filter((x) => x.name);
      if (!items.length) { toast.error("CSV vide ou invalide"); return; }
      try {
        const r = await api.post(`/admin/clients/${client.id}/agencies/import`, { items });
        toast.success(`${r.data.created} agences importées`);
        onImported();
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    };
    reader.readAsText(f);
    e.target.value = "";
  };

  return (
    <div className="space-y-6" data-testid="client-insights">
      {/* Actions header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <h2 className="font-heading font-bold text-2xl">{client.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">Vue d'ensemble — avis Google par agence</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="import-csv-btn" className="flex items-center gap-1.5 text-sm border border-border px-3 py-2.5 hover:border-foreground/40 transition-colors">
            <Upload size={15} /> Importer CSV
          </button>
          <button onClick={analyze} disabled={analyzing} data-testid="analyze-client-btn" className="flex items-center gap-1.5 text-sm border border-accent/40 text-accent px-3 py-2.5 hover:bg-accent/5 transition-colors disabled:opacity-60">
            {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Analyser les thèmes (IA)
          </button>
          <button onClick={sync} disabled={syncing} data-testid="sync-all-btn" className="flex items-center gap-2 text-sm bg-warm text-background font-semibold px-5 py-2.5 hover:bg-warm-soft transition-colors glow-warm disabled:opacity-60">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} Synchroniser les avis Google
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Building2} label="Agences" value={insights?.agency_count ?? "…"} />
        <Kpi icon={Star} label="Note moyenne" value={insights?.avg_rating ?? "—"} />
        <Kpi icon={MessageSquare} label="Total avis" value={insights?.total_reviews ?? "…"} />
        <Kpi icon={ScanLine} label="Scans totaux" value={insights?.total_scans ?? "…"} />
      </div>

      {/* Rankings */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-surface border border-border">
          <div className="flex items-center gap-2 p-4 border-b border-border">
            <Award size={16} className="text-warm" />
            <h3 className="font-heading font-semibold text-sm">Mieux notées</h3>
          </div>
          <div className="divide-y divide-border" data-testid="ranking-best">
            {(insights?.best || []).map((a) => <RankRow key={a.id} a={a} onOpen={onOpenAgency} best />)}
          </div>
        </div>
        <div className="bg-surface border border-border">
          <div className="flex items-center gap-2 p-4 border-b border-border">
            <TrendingDown size={16} className="text-destructive" />
            <h3 className="font-heading font-semibold text-sm">À surveiller (moins notées)</h3>
          </div>
          <div className="divide-y divide-border" data-testid="ranking-worst">
            {(insights?.worst || []).map((a) => <RankRow key={a.id} a={a} onOpen={onOpenAgency} />)}
          </div>
        </div>
      </div>

      {/* AI themes */}
      <div className="bg-surface border border-border p-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Sparkles size={17} className="text-accent" /> Thèmes des avis (IA) — global</h3>
          {!analysis && !analyzing && <button onClick={analyze} className="text-xs text-accent hover:underline">Lancer l'analyse</button>}
        </div>
        {!analysis && !analyzing ? (
          <p className="text-sm text-muted-foreground mt-3">Lance l'analyse IA pour extraire les thèmes récurrents et le sentiment de tous les avis du client.</p>
        ) : (
          <AiAnalysisView analysis={analysis} loading={analyzing} />
        )}
      </div>

      {/* Latest reviews */}
      <div className="bg-surface border border-border p-6">
        <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-4"><MessageSquare size={17} className="text-warm" /> Derniers avis · toutes agences</h3>
        {latest.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun avis disponible.</p>
        ) : (
          <div className="space-y-3" data-testid="latest-reviews">
            {latest.slice(0, 12).map((rv, i) => (
              <div key={i} className="border border-border p-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-medium">{rv.author || "Anonyme"}</span>
                  <Stars rating={rv.rating} />
                  <span className="text-[11px] text-muted-foreground">· {rv.agency}</span>
                </div>
                <p className="text-sm text-muted-foreground">{rv.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AgencyDetail({ agency, onChanged, onDeleted, onBack }) {
  const [analytics, setAnalytics] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [url, setUrl] = useState(agency.google_review_url);
  const [refreshing, setRefreshing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const redirectUrl = `${BACKEND_URL}/api/r/${agency.code}`;

  const load = async () => {
    try {
      const [a, r, an] = await Promise.all([
        api.get(`/admin/agencies/${agency.id}/analytics?days=30`),
        api.get(`/admin/agencies/${agency.id}/reviews`),
        api.get(`/admin/agencies/${agency.id}/analysis`),
      ]);
      setAnalytics(a.data);
      setReviews(r.data);
      setAnalysis(an.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    setUrl(agency.google_review_url);
    setAnalytics(null);
    setReviews(null);
    setAnalysis(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency.id]);

  const analyzeAgency = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/admin/agencies/${agency.id}/analyze`);
      setAnalysis(r.data);
      toast.success("Analyse IA terminée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const saveUrl = async () => {
    try {
      await api.put(`/admin/agencies/${agency.id}`, { google_review_url: url });
      toast.success("Destination du QR mise à jour");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleActive = async () => {
    try {
      await api.put(`/admin/agencies/${agency.id}`, { active: !agency.active });
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer l'agence « ${agency.name} » et ses statistiques ?`)) return;
    try {
      await api.delete(`/admin/agencies/${agency.id}`);
      toast.success("Agence supprimée");
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const refreshReviews = async () => {
    setRefreshing(true);
    try {
      await api.post(`/admin/agencies/${agency.id}/reviews/refresh`);
      toast.success("Avis Google synchronisés");
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById(`qr-${agency.id}`)?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `qr-${agency.name.replace(/\s+/g, "-").toLowerCase()}.png`;
    link.click();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(redirectUrl);
      toast.success("Lien copié");
    } catch {
      toast.error("Copie indisponible (HTTPS requis)");
    }
  };

  return (
    <div className="space-y-6" data-testid={`agency-detail-${agency.id}`}>
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
        <div>
          {onBack && (
            <button onClick={onBack} data-testid="back-to-client" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ChevronLeft size={14} /> Vue d'ensemble
            </button>
          )}
          <h2 className="font-heading font-bold text-2xl">{agency.name}</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
            <Building2 size={13} /> {agency.city || "—"}
            <span className={`ml-2 font-mono text-[9px] tracking-wider uppercase px-2 py-0.5 ${agency.active ? "bg-accent/15 text-accent" : "bg-white/10 text-muted-foreground"}`}>
              {agency.active ? "Actif" : "Désactivé"}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={toggleActive} data-testid="agency-toggle-active" className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:border-warm/50 hover:text-warm transition-colors">
            <Power size={14} /> {agency.active ? "Désactiver" : "Activer"}
          </button>
          <button onClick={remove} data-testid="agency-delete" className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:border-destructive/60 hover:text-destructive transition-colors">
            <Trash2 size={14} /> Supprimer
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* QR card */}
        <div className="bg-surface border border-border p-6 flex flex-col items-center">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground self-start mb-4 flex items-center gap-2"><QrCode size={13} /> QR code du chevalet</p>
          <div id={`qr-${agency.id}`} className="bg-white p-4 rounded">
            <QRCodeCanvas value={redirectUrl} size={172} bgColor="#ffffff" fgColor="#0a0908" level="M" />
          </div>
          <div className="flex gap-2 mt-5 w-full">
            <button onClick={downloadQR} data-testid="qr-download" className="flex-1 flex items-center justify-center gap-2 bg-accent text-background font-semibold px-3 py-2.5 text-sm hover:bg-accent-hover transition-colors">
              <Download size={15} /> PNG
            </button>
            <button onClick={copyLink} data-testid="qr-copy" className="flex items-center justify-center gap-2 border border-border px-3 py-2.5 text-sm hover:border-foreground/40 transition-colors">
              <Copy size={15} />
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3 break-all font-mono text-center">{redirectUrl}</p>
        </div>

        {/* Editable destination + KPIs */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-surface border border-border p-5">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Destination de l'avis Google (modifiable)</label>
            <div className="flex gap-2">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="agency-url-input"
                className="flex-1 bg-background border border-border px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-accent transition-colors"
              />
              <button onClick={saveUrl} data-testid="agency-url-save" className="flex items-center gap-1.5 bg-warm text-background font-semibold px-4 py-2.5 text-sm hover:bg-warm-soft transition-colors">
                <Save size={15} /> Enregistrer
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Le QR ne change jamais : modifie la destination sans réimprimer le chevalet.</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Kpi icon={ScanLine} label="Scans totaux" value={agency.scan_count ?? 0} />
            <Kpi icon={TrendingUp} label="30 derniers j." value={analytics ? analytics.period_total : "…"} />
            <div className="bg-surface border border-border p-4">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Note Google</span>
              <div className="mt-2"><Stars rating={agency.rating} /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scan chart */}
      <div className="bg-surface border border-border p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><ScanLine size={17} className="text-accent" /> Scans des avis · 30 jours</h3>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={analytics?.daily || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00e5ff" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#00e5ff" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2c2620" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#6b6359" fontSize={11} tickLine={false} />
            <YAxis stroke="#6b6359" fontSize={11} tickLine={false} allowDecimals={false} />
            <Tooltip {...chartTooltip} labelFormatter={fmtDay} />
            <Area type="monotone" dataKey="scans" stroke="#00e5ff" strokeWidth={2} fill="url(#scanGrad)" name="Scans" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Reviews monitoring */}
      <div className="bg-surface border border-border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Star size={17} className="text-warm" /> Avis Google · évolution</h3>
          <div className="flex items-center gap-3">
            {reviews?.current?.review_count != null && (
              <span className="text-sm text-muted-foreground">{reviews.current.review_count} avis</span>
            )}
            <a href={agency.google_review_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-warm inline-flex items-center gap-1"><ExternalLink size={13} /> Fiche</a>
            <button onClick={refreshReviews} disabled={refreshing} data-testid="reviews-refresh" className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:border-warm/50 hover:text-warm transition-colors disabled:opacity-60">
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Synchroniser
            </button>
            <button onClick={analyzeAgency} disabled={analyzing} data-testid="agency-analyze" className="flex items-center gap-1.5 text-xs border border-accent/40 text-accent px-3 py-2 hover:bg-accent/5 transition-colors disabled:opacity-60">
              {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Thèmes IA
            </button>
          </div>
        </div>

        {reviews && !reviews.has_key && (
          <div className="mb-5 border border-warm/30 bg-warm/5 px-4 py-3 text-xs text-warm">
            Suivi en direct désactivé — ajoutez une clé Google Places API pour synchroniser les avis réels. Données affichées : démonstration.
          </div>
        )}

        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={reviews?.trend || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2c2620" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#6b6359" fontSize={11} tickLine={false} />
            <YAxis yAxisId="left" domain={[4, 5]} ticks={[4, 4.25, 4.5, 4.75, 5]} tickFormatter={(v) => v.toFixed(2)} stroke="#ff9e64" fontSize={11} tickLine={false} width={42} />
            <YAxis yAxisId="right" orientation="right" domain={["dataMin - 10", "dataMax + 10"]} allowDecimals={false} stroke="#00e5ff" fontSize={11} tickLine={false} width={40} />
            <Tooltip {...chartTooltip} labelFormatter={fmtDay} />
            <Line yAxisId="left" type="monotone" dataKey="rating" stroke="#ff9e64" strokeWidth={2.5} dot={false} name="Note" />
            <Line yAxisId="right" type="monotone" dataKey="review_count" stroke="#00e5ff" strokeWidth={2} dot={false} name="Nb d'avis" />
          </LineChart>
        </ResponsiveContainer>

        <div className="flex items-center gap-5 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-warm inline-block" /> Note /5</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-accent inline-block" /> Nombre d'avis</span>
        </div>

        <AiAnalysisView analysis={analysis} loading={analyzing} />

        {reviews?.recent?.length > 0 && (
          <div className="mt-6 space-y-3">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Avis récents</p>
            {reviews.recent.map((rv, i) => (
              <div key={i} className="border border-border p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">{rv.author || "Anonyme"}</span>
                  <Stars rating={rv.rating} />
                </div>
                <p className="text-sm text-muted-foreground">{rv.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCockpit() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [newClient, setNewClient] = useState("");
  const [showAgencyForm, setShowAgencyForm] = useState(false);
  const [agencyForm, setAgencyForm] = useState({ name: "", city: "", google_review_url: "", business_name: "" });
  const [loading, setLoading] = useState(true);

  const loadOverview = async () => {
    try {
      const res = await api.get("/admin/cockpit/overview");
      setOverview(res.data);
    } catch (err) { /* noop */ }
  };

  const loadClients = async () => {
    try {
      const res = await api.get("/admin/review-clients");
      setClients(res.data);
      if (res.data.length && !selectedClient) setSelectedClient(res.data[0]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAgencies = async (clientId) => {
    try {
      const res = await api.get(`/admin/clients/${clientId}/agencies`);
      setAgencies(res.data);
      setSelectedAgency((prev) => (prev ? res.data.find((a) => a.id === prev.id) || null : null));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { loadOverview(); loadClients(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selectedClient) loadAgencies(selectedClient.id); /* eslint-disable-next-line */ }, [selectedClient]);

  const addClient = async (e) => {
    e.preventDefault();
    if (!newClient.trim()) return;
    try {
      const res = await api.post("/admin/review-clients", { name: newClient.trim() });
      setNewClient("");
      await loadClients();
      setSelectedClient(res.data);
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Supprimer le client « ${c.name} » et toutes ses agences ?`)) return;
    try {
      await api.delete(`/admin/review-clients/${c.id}`);
      setSelectedClient(null);
      setSelectedAgency(null);
      await loadClients();
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const addAgency = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/agencies", { client_id: selectedClient.id, ...agencyForm });
      setShowAgencyForm(false);
      setAgencyForm({ name: "", city: "", google_review_url: "", business_name: "" });
      await loadAgencies(selectedClient.id);
      setSelectedAgency(res.data);
      loadClients();
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const refreshAfterChange = async () => {
    await loadAgencies(selectedClient.id);
    loadOverview();
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const kpis = useMemo(() => ([
    { icon: Users, label: "Clients", value: overview?.clients ?? "…" },
    { icon: Building2, label: "Agences", value: overview?.agencies ?? "…" },
    { icon: ScanLine, label: "Scans totaux", value: overview?.scans ?? "…" },
    { icon: Star, label: "Note moyenne", value: overview?.avg_rating ?? "—" },
  ]), [overview]);

  return (
    <div className="min-h-screen grain relative">
      <NavHeader onLogout={doLogout} />
      <main className="max-w-[100rem] mx-auto px-6 lg:px-8 py-8 relative z-10">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl">Cockpit Avis Google</h1>
            <p className="text-sm text-muted-foreground mt-1">Gérez vos chevalets QR et suivez les avis Google, agence par agence.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((k) => <Kpi key={k.label} {...k} />)}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-surface border border-border">
                <div className="p-4 border-b border-border">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Clients</p>
                  <form onSubmit={addClient} className="flex gap-2">
                    <input value={newClient} onChange={(e) => setNewClient(e.target.value)} placeholder="Nouveau client" data-testid="new-client-input" className="flex-1 bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
                    <button type="submit" data-testid="add-client-btn" className="bg-accent text-background px-3 hover:bg-accent-hover transition-colors"><Plus size={16} /></button>
                  </form>
                </div>
                <div className="divide-y divide-border max-h-[280px] overflow-y-auto">
                  {clients.map((c) => (
                    <div key={c.id} className={`flex items-center justify-between px-4 py-3 cursor-pointer group ${selectedClient?.id === c.id ? "bg-accent/5" : "hover:bg-white/5"}`} onClick={() => { setSelectedClient(c); setSelectedAgency(null); }} data-testid={`client-item-${c.id}`}>
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${selectedClient?.id === c.id ? "text-accent font-medium" : "text-foreground"}`}>{c.name}</p>
                        <p className="text-[11px] text-muted-foreground">{c.agency_count} agence{c.agency_count > 1 ? "s" : ""}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); deleteClient(c); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>

              {selectedClient && (
                <div className="bg-surface border border-border">
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Agences</p>
                    <button onClick={() => setShowAgencyForm(true)} data-testid="add-agency-btn" className="flex items-center gap-1 text-xs text-warm hover:text-warm-soft"><Plus size={14} /> Ajouter</button>
                  </div>
                  <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                    {agencies.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground text-center">Aucune agence.</p>
                    ) : agencies.map((a) => (
                      <button key={a.id} onClick={() => setSelectedAgency(a)} data-testid={`agency-item-${a.id}`} className={`w-full text-left px-4 py-3 ${selectedAgency?.id === a.id ? "bg-warm/5" : "hover:bg-white/5"}`}>
                        <p className={`text-sm truncate ${selectedAgency?.id === a.id ? "text-warm font-medium" : "text-foreground"}`}>{a.name}</p>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-2">
                          <span className="inline-flex items-center gap-1"><ScanLine size={11} /> {a.scan_count}</span>
                          {a.rating != null && <span className="inline-flex items-center gap-1"><Star size={11} className="fill-warm text-warm" /> {a.rating}</span>}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Detail */}
            <div>
              {selectedAgency ? (
                <AgencyDetail agency={selectedAgency} onChanged={refreshAfterChange} onDeleted={() => { setSelectedAgency(null); refreshAfterChange(); }} onBack={() => setSelectedAgency(null)} />
              ) : selectedClient ? (
                <ClientInsights
                  client={selectedClient}
                  onOpenAgency={(id) => setSelectedAgency(agencies.find((a) => a.id === id) || null)}
                  onImported={() => { loadAgencies(selectedClient.id); loadClients(); loadOverview(); }}
                />
              ) : (
                <div className="border border-border bg-surface text-center py-24">
                  <QrCode size={40} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Sélectionnez un client.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Add agency modal */}
      {showAgencyForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-surface border border-border w-full max-w-lg my-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="font-heading font-semibold text-lg">Nouvelle agence — {selectedClient?.name}</h2>
              <button onClick={() => setShowAgencyForm(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <form onSubmit={addAgency} className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Nom de l'agence *</label>
                  <input required value={agencyForm.name} onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })} data-testid="agency-form-name" placeholder="Groupama Lyon" className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-accent" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Ville</label>
                  <input value={agencyForm.city} onChange={(e) => setAgencyForm({ ...agencyForm, city: e.target.value })} data-testid="agency-form-city" placeholder="Lyon" className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-accent" />
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Lien avis Google *</label>
                <input required value={agencyForm.google_review_url} onChange={(e) => setAgencyForm({ ...agencyForm, google_review_url: e.target.value })} data-testid="agency-form-url" placeholder="https://g.page/r/...?review ou lien Google Maps" className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-accent" />
                <p className="text-[11px] text-muted-foreground mt-2">C'est la page vers laquelle le QR redirigera (modifiable plus tard).</p>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Nom Google (pour le suivi des avis)</label>
                <input value={agencyForm.business_name} onChange={(e) => setAgencyForm({ ...agencyForm, business_name: e.target.value })} data-testid="agency-form-business" placeholder="Groupama Lyon Part-Dieu" className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-accent" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" data-testid="agency-form-save" className="flex-1 bg-accent text-background font-semibold px-6 py-3 hover:bg-accent-hover transition-colors">Créer l'agence + QR</button>
                <button type="button" onClick={() => setShowAgencyForm(false)} className="px-6 py-3 border border-border hover:border-foreground/40 transition-colors">Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
